import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  BusinessOutlined,
  LockOutlined,
  PersonOutline,
  SecurityOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import api from "../api/api";
import AuthLayout, { BRAND_ORANGE, BRAND_ORANGE_DARK, SIDEBAR_BLUE } from "../components/AuthLayout";
import { sessionManager } from "../utils/sessionManager";
import { lerLoginSalvo, salvarPreferenciaLogin } from "../utils/loginRemember";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    borderRadius: "12px",
    backgroundColor: "#fff",
    transition: "box-shadow 0.18s ease, border-color 0.18s ease",
    "& fieldset": {
      borderColor: "#d0d5dd",
    },
    "&:hover fieldset": {
      borderColor: BRAND_ORANGE,
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(255, 107, 0, 0.14)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND_ORANGE,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#667085",
    fontWeight: 700,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: BRAND_ORANGE,
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    fontWeight: 600,
  },
};

const Login = () => {
  const navigate = useNavigate();
  const [login, setLogin] = useState("");
  const [dominio, setDominio] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [lembrarAcesso, setLembrarAcesso] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sessionExpired") === "true") {
      setError("Sua sessão expirou. Faça login novamente.");
      window.history.replaceState({}, document.title, "/login");
    }

    const salvo = lerLoginSalvo();
    if (salvo) {
      setDominio(salvo.dominio);
      setLogin(salvo.login);
      setLembrarAcesso(true);
    }
  }, []);

  const validate = () => {
    const nextErrors = {};

    if (!String(dominio || "").trim()) {
      nextErrors.dominio = "Informe o domínio do estabelecimento.";
    }

    if (!String(login || "").trim()) {
      nextErrors.login = "Informe seu usuário ou e-mail.";
    }

    if (!senha) {
      nextErrors.senha = "Informe sua senha.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validate()) return;

    setLoading(true);

    const dominioNorm = String(dominio || "").toLowerCase().trim();

    try {
      await sessionManager.encerrarSessaoAtualSeExistir();

      const response = await api.post("/auth/login", { login, senha, dominio: dominioNorm });

      sessionManager.salvarSessao(response.data.usuario);

      salvarPreferenciaLogin({
        dominio: dominioNorm,
        login: String(login || "").trim(),
        remember: lembrarAcesso,
      });

      localStorage.setItem("userName", response.data.usuario.nome);

      if (response.data.usuario.estabelecimento_id) {
        localStorage.setItem("estabelecimento_id", String(response.data.usuario.estabelecimento_id));
        try {
          window.dispatchEvent(
            new CustomEvent("fz:estabelecimento:changed", {
              detail: { id: String(response.data.usuario.estabelecimento_id) },
            })
          );
        } catch (eventError) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("Não foi possível sincronizar estabelecimento:", eventError?.message);
          }
        }
      }

      navigate(sessionManager.obterDestinoPosLogin(response.data.usuario), { replace: true });
    } catch (err) {
      const mensagem = err.response?.data?.mensagem || "Não foi possível entrar. Confira domínio, usuário e senha.";
      setError(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const clearFieldError = (field) => {
    if (!fieldErrors[field]) return;
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  return (
    <AuthLayout
      showBrandPanel
      title="Acesse o FilaZero Admin"
      subtitle="Gerencie agenda, equipe e atendimentos em um só lugar."
    >
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: "12px",
            textAlign: "left",
            fontWeight: 700,
          }}
        >
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Domínio"
          type="text"
          variant="outlined"
          value={dominio}
          onChange={(e) => {
            setDominio(e.target.value);
            clearFieldError("dominio");
          }}
          fullWidth
          required
          placeholder="nome estabelecimento"
          helperText={fieldErrors.dominio || "Identificador do seu estabelecimento."}
          error={Boolean(fieldErrors.dominio)}
          InputLabelProps={{ shrink: true }}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessOutlined sx={{ color: "#667085" }} />
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        <TextField
          label="Usuário ou e-mail"
          type="text"
          variant="outlined"
          value={login}
          onChange={(e) => {
            setLogin(e.target.value);
            clearFieldError("login");
          }}
          fullWidth
          required
          placeholder="ex: leandro@empresa.com"
          helperText={fieldErrors.login || "Use seu usuário cadastrado ou e-mail de acesso."}
          error={Boolean(fieldErrors.login)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutline sx={{ color: "#667085" }} />
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        <TextField
          label="Senha"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={senha}
          onChange={(e) => {
            setSenha(e.target.value);
            clearFieldError("senha");
          }}
          fullWidth
          required
          placeholder="Digite sua senha"
          helperText={fieldErrors.senha || "Sua senha permanece protegida."}
          error={Boolean(fieldErrors.senha)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined sx={{ color: "#667085" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((current) => !current)}
                  edge="end"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  sx={{ color: "#667085" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={lembrarAcesso}
              onChange={(e) => setLembrarAcesso(e.target.checked)}
              sx={{
                color: "#98a2b3",
                "&.Mui-checked": { color: BRAND_ORANGE },
              }}
            />
          }
          label="Lembrar domínio e usuário neste dispositivo"
          sx={{
            alignSelf: "flex-start",
            mx: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#667085",
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          endIcon={!loading ? <ArrowForward /> : null}
          sx={{
            mt: 0.5,
            minHeight: 54,
            fontSize: "1rem",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 800,
            bgcolor: BRAND_ORANGE,
            boxShadow: "0 14px 30px rgba(255, 107, 0, 0.24)",
            "&:hover": {
              bgcolor: BRAND_ORANGE_DARK,
              boxShadow: "0 16px 34px rgba(255, 107, 0, 0.28)",
            },
            "&.Mui-disabled": {
              bgcolor: "#fed7aa",
              color: "#fff",
            },
          }}
        >
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <CircularProgress size={20} sx={{ color: "#fff" }} />
              Entrando...
            </span>
          ) : (
            "Entrar no painel"
          )}
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            mt: 0.5,
            color: "#667085",
          }}
        >
          <SecurityOutlined sx={{ fontSize: 18, color: SIDEBAR_BLUE }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Acesso seguro ao painel administrativo.
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Esqueceu sua senha?{" "}
          <Typography
            component="span"
            sx={{
              color: BRAND_ORANGE,
              fontWeight: 800,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => navigate("/esqueci-senha")}
          >
            Recuperar acesso
          </Typography>
        </Typography>
      </Box>
    </AuthLayout>
  );
};

export default Login;
